import { Breakpoint, ButtonType, UiComponentSize } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import {
    StyledArrowLeftIcon,
    StyledBreadcrumb,
    StyledButton,
    StyledContainer,
    StyledExternalLink,
    StyledLink,
    StyledLinkContainer,
    StyledText,
} from 'app/components/breadcrumb/breadcrumb.styled';
import { i18n } from 'app/i18n';
import { RequestStatus } from 'app/pages/order/redux/model/order-request';
import {
    selectIsPurchasingSingleUnit,
    selectOrderRequests,
    selectSelectedHouseType,
} from 'app/pages/order/redux/order-selector';
import { arrowLeftImageProps } from 'app/resources/images';
import { NavigationService } from 'app/services/navigation-service';
import { BreadcrumbUtils } from 'app/utils/breadcrumb-utils';
import { useLargeTabletMediaQuery } from 'app/utils/breakpoints-utils';

export const Breadcrumb: FunctionComponent = () => {
    const navigate = useNavigate();
    const path: string = useLocation().pathname;
    const houseType = useSelector(selectSelectedHouseType);
    const purchasingSingleUnit: boolean = useSelector(selectIsPurchasingSingleUnit);
    const { externalBreadcrumbs, internalBreadcrumbs } = BreadcrumbUtils.getBreadcrumbs(
        path,
        houseType,
        purchasingSingleUnit,
    );
    const orderRequests = useSelector(selectOrderRequests);
    const requestsPending = orderRequests.some((request) => request.status === RequestStatus.PENDING);

    const SHOW_BREADCRUMB = [
        NavigationService.HOUSING_SELECT,
        NavigationService.PRODUCT_DETAILS,
        NavigationService.SUMMARY,
    ];

    const isOnLargeTablet = useLargeTabletMediaQuery();

    if (
        !SHOW_BREADCRUMB.includes(path) ||
        (BreadcrumbUtils.isFirstInternalBreadcrumb(path, internalBreadcrumbs) && !isOnLargeTablet) ||
        requestsPending
    ) {
        return null;
    }

    const handleClickBack = () => {
        navigate(BreadcrumbUtils.getPreviousBreadcrumbPath(path, internalBreadcrumbs));
    };

    const renderMobileBreadcrumbItems = () => (
        <StyledButton
            buttonType={ButtonType.TERTIARY}
            uiComponentSize={UiComponentSize.MEDIUM}
            onClick={handleClickBack}
            iconWrapper={{
                icon: <StyledArrowLeftIcon {...arrowLeftImageProps} />,
                placeIconBeforeText: true,
            }}
        >
            {i18n.components.breadcrumb.back}
        </StyledButton>
    );

    const renderDesktopBreadcrumbItems = () => (
        <>
            {externalBreadcrumbs.map((breadcrumb, index) => (
                <StyledLinkContainer key={index}>
                    <StyledExternalLink variant="paragraph1" maxBreakpoint={Breakpoint.MOBILE} href={breadcrumb.path}>
                        {breadcrumb.text}
                    </StyledExternalLink>
                </StyledLinkContainer>
            ))}
            {internalBreadcrumbs.map((breadcrumb, index) => (
                <StyledLinkContainer key={index}>
                    {internalBreadcrumbs.length - 1 === index ? (
                        <StyledText variant="paragraph1" component="p" maxBreakpoint={Breakpoint.MOBILE}>
                            {breadcrumb.text}
                        </StyledText>
                    ) : (
                        <StyledLink to={breadcrumb.path}>{breadcrumb.text}</StyledLink>
                    )}
                </StyledLinkContainer>
            ))}
        </>
    );

    return (
        <StyledBreadcrumb>
            <StyledContainer>
                {isOnLargeTablet ? renderDesktopBreadcrumbItems() : renderMobileBreadcrumbItems()}
            </StyledContainer>
        </StyledBreadcrumb>
    );
};
